import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Loader2, Target, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, formatDate } from "@/lib/api";
import { Empty, Evidence } from "@/components/AnalyticsCards";

const Row = ({ label, children }) => (
  <div className="grid grid-cols-[92px_1fr] gap-2 text-sm">
    <span className="text-[11px] font-bold uppercase tracking-wider text-[#71717A] pt-0.5">{label}</span>
    <div className="text-[#E4E4E7]">{children}</div>
  </div>
);

export function FocusCard({ item, index, active, onSaved }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const isActive = active.some((a) => a.title === item.title);
  const save = async (replaceId) => {
    setBusy(true);
    try {
      await api.post("/focus", { title: item.title, why: item.why || "", drill: item.drill || "", topic: item.topic, replace_id: replaceId });
      qc.invalidateQueries({ queryKey: ["focus"] });
      onSaved();
      toast.success("Saved. It'll be on your Log page before your next session.");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card p-4 relative overflow-hidden space-y-2.5" data-testid={`analytics-focus-${index}`}>
      <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#E50914]" />
      <Row label="Focus"><span className="font-semibold">{item.title}</span></Row>
      <Row label="Why">{item.why || item.detail}</Row>
      {item.drill && <Row label="Next session">{item.drill}</Row>}
      <Row label="Evidence">
        <div className="space-y-1.5">
          {item.evidence.map((e) => <Evidence key={e.session_id} ev={e} />)}
          {item.quiz && <div className="text-[12.5px] text-[#A1A1AA]" data-testid="analytics-focus-quiz">Quiz: {item.quiz.right}/{item.quiz.total} correct on {item.quiz.label} ({item.quiz.confidence})</div>}
        </div>
      </Row>
      {isActive ? (
        <div className="flex items-center gap-1.5 text-xs text-[#22C55E] pt-1" data-testid={`analytics-focus-active-${index}`}><Check size={14} /> Active focus, shown on your Log page</div>
      ) : active.length >= 3 ? (
        <select disabled={busy} defaultValue="" onChange={(e) => e.target.value && save(e.target.value)} className="w-full h-10 rounded-lg bg-[#18181B] border border-[#27272A] text-sm px-3" data-testid={`analytics-focus-replace-${index}`}>
          <option value="" disabled>Replace an active focus with this…</option>
          {active.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
        </select>
      ) : (
        <button onClick={() => save()} disabled={busy} className="btn-secondary w-full h-10 text-sm" data-testid={`analytics-focus-save-${index}`}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <><Target size={15} /> Focus on this next session</>}
        </button>
      )}
    </div>
  );
}

export function GameList({ game }) {
  const [open, setOpen] = useState(false);
  if (!game.length) return <Empty testid="analytics-game-empty">Your positions and techniques will appear here as you log sessions.</Empty>;
  const groups = {};
  game.forEach((g) => (groups[g.group] = groups[g.group] || []).push(g));
  const order = Object.entries(groups).sort((a, b) => b[1].reduce((n, g) => n + g.count, 0) - a[1].reduce((n, g) => n + g.count, 0));
  const shown = open ? order : order.slice(0, 4);
  const max = game[0].count;
  return (
    <div className="card p-4 space-y-4" data-testid="analytics-game">
      <p className="text-[11px] text-[#71717A]">Bars show how many sessions mention each one (training frequency, not skill).</p>
      {shown.map(([group, items]) => (
        <div key={group}>
          <div className="eyebrow mb-2">{group}</div>
          <div className="space-y-2.5">
            {items.map((g) => (
              <Link key={g.name} to={`/tag/${encodeURIComponent(g.name)}`} className="block" data-testid={`analytics-game-item-${g.name.replace(/\s+/g, "-")}`}>
                <div className="flex justify-between gap-2 text-sm mb-1">
                  <span className="capitalize flex items-center gap-1.5">{g.name}
                    {g.trend === "more" && <TrendingUp size={13} className="text-[#A1A1AA]" aria-label="Logged more often recently" />}
                    {g.trend === "less" && <TrendingDown size={13} className="text-[#71717A]" aria-label="Logged less often recently" />}
                  </span>
                  <span className="text-[11px] text-[#A1A1AA] shrink-0">{g.count} session{g.count === 1 ? "" : "s"}</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/5"><div className="h-full rounded-full bg-white/60" style={{ width: `${(g.count / max) * 100}%` }} /></div>
                {(g.went_well > 0 || g.struggled > 0) && (
                  <div className="text-[11px] mt-1 text-[#71717A]">
                    {g.went_well > 0 && <span className="text-[#22C55E]">Went well in {g.went_well}</span>}
                    {g.went_well > 0 && g.struggled > 0 && " · "}
                    {g.struggled > 0 && <span className="text-[#FF6B6B]">Struggled in {g.struggled}</span>}
                  </div>
                )}
              </Link>
            ))}
          </div>
        </div>
      ))}
      {order.length > 4 && (
        <button onClick={() => setOpen(!open)} className="w-full text-xs text-[#A1A1AA] flex items-center justify-center gap-1" data-testid="analytics-game-toggle">
          {open ? "Show less" : `Show all ${order.length} groups`} <ChevronDown size={14} className={open ? "rotate-180" : ""} />
        </button>
      )}
      <p className="text-[11px] text-[#71717A]">"Went well" and "Struggled" count sessions where you wrote that. Tap one to see those sessions. Arrows show if you've logged it more or less often recently.</p>
    </div>
  );
}

export function ProgressBlock({ a }) {
  const items = [];
  a.turnarounds.forEach((t) => items.push(
    <div key={`t-${t.name}`} className="card p-4" data-testid="analytics-turnaround">
      <div className="font-semibold capitalize flex items-center gap-2"><TrendingUp size={15} className="text-[#22C55E]" />{t.name}</div>
      <p className="text-[12.5px] text-[#A1A1AA] mt-1">
        Logged as a struggle on <Link className="underline" to={`/session/${t.struggled.session_id}`}>{formatDate(t.struggled.date)}</Link>, then as going well on <Link className="underline" to={`/session/${t.went_well.session_id}`}>{formatDate(t.went_well.date)}</Link>. Self-reported, one good session isn't proof it's fixed.
      </p>
    </div>,
  ));
  const quizTrends = a.quiz.topics.filter((t) => t.trend && Math.abs(t.trend.recent - t.trend.earlier) >= 10);
  if (quizTrends.length) items.push(
    <div key="quiz" className="card p-4" data-testid="analytics-quiz-trends">
      <div className="eyebrow mb-2">Quiz accuracy, earlier vs recent answers</div>
      {quizTrends.map((t) => (
        <div key={t.topic} className="flex justify-between text-sm py-1">
          <span>{t.label}</span>
          <span className={t.trend.recent > t.trend.earlier ? "text-[#22C55E]" : "text-[#FF6B6B]"}>{t.trend.earlier}% → {t.trend.recent}% <span className="text-[11px] text-[#71717A]">({t.trend.earlier_n}/{t.trend.recent_n} answers)</span></span>
        </div>
      ))}
    </div>,
  );
  if (a.shift && (a.shift.new.length || a.shift.dropped.length)) items.push(
    <div key="shift" className="card p-4 text-sm space-y-1" data-testid="analytics-shift">
      <div className="eyebrow mb-1">What you're training, earlier vs recent sessions</div>
      {a.shift.new.length > 0 && <div><span className="text-[#A1A1AA]">New recently: </span><span className="capitalize">{a.shift.new.join(", ")}</span></div>}
      {a.shift.dropped.length > 0 && <div><span className="text-[#A1A1AA]">Not logged lately: </span><span className="capitalize">{a.shift.dropped.join(", ")}</span></div>}
    </div>,
  );
  a.focuses.filter((f) => f.checkins.length).forEach((f) => items.push(
    <div key={`f-${f.id}`} className="card p-4" data-testid="analytics-focus-progress">
      <div className="font-semibold flex items-center gap-2"><Target size={15} className="text-[#E50914]" />{f.title}{f.status === "completed" && <span className="text-[10px] uppercase tracking-wider text-[#22C55E]">Completed</span>}</div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {f.checkins.map((c) => <span key={c.session_id} className={`text-[11px] rounded px-2 py-0.5 ${c.practised === "yes" ? "bg-[#22C55E]/15 text-[#22C55E]" : c.practised === "partly" ? "bg-[#F59E0B]/15 text-[#F59E0B]" : "bg-white/5 text-[#A1A1AA]"}`}>{formatDate(c.date)}: {c.practised === "yes" ? "practised" : c.practised === "partly" ? "partly" : "not practised"}</span>)}
      </div>
    </div>,
  ));
  return items;
}
